// App.js
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import HostProfilePageBigPicture from './pages/HostProfilePageBigPicture';
import Navbar from './components/Navbar';
import BuyerDashboard from './pages/BuyerDashboard';
import HostDashboard from './pages/HostDashboard';
import ArtistManager from './pages/ArtistManager';
import HostManager from './pages/HostManager';
import BuyerManager from './pages/BuyerManager';
import ArtworkManager from './pages/ArtworkManager';
import AdminPage from './pages/AdminPage';
import LoginPage from './pages/LoginPage';
import NotificationsPage from './pages/NotificationsPage';
import RegisterPage from './pages/RegisterPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ResetPasswordConfirmPage from './pages/ResetPasswordConfirmPage';
import ArtistDashboard from './pages/ArtistDashboard';
import ArtworkCreatePage from './pages/ArtworkCreatePage';
import ArtistProfileSetupPage from './pages/ArtistProfileSetupPage';
import HostProfileSetupPage from './pages/HostProfileSetupPage';
import BuyerProfileSetupPage from './pages/BuyerProfileSetupPage';
import HostRequestsPage from './pages/HostRequestsPage';
import NearbyArtworksPage from './pages/NearbyArtworksPage';
import HomePage from './pages/HomePage';
import { useAuth } from './hooks/useAuth';
import GlobalBlurredCollage from './components/GlobalBlurredCollage';
import AppErrorBoundary from "./components/AppErrorBoundary";
import { useChat } from './context/ChatContext';
import MessagesDrawer from './components/MessagesDrawer';
import { CheckoutSuccess } from './pages/CheckoutSuccess';
import MyPurchases from "./pages/MyPurchases";
import ArtworkDetail from "./pages/ArtworkDetail";
import CompleteSignupPage from './pages/CompleteSignupPage';
import SessionProvider from './session/SessionProvider';
import ArtistProfilePageBigPicture from './pages/ArtistProfilePageBigPicture';
import HelpDrawer from './components/HelpDrawer';

function defaultRouteForRole(role) {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'artist':
      return '/dashboard/artist';
    case 'host':
      return '/dashboard/host';
    case 'buyer':
      return '/dashboard/buyer';
    default:
      return '/';
  }
}

function RequireRole({ auth, allowedRole, children }) {
  const location = useLocation();
  const persistedToken = localStorage.getItem('access_token');
  const persistedRole = localStorage.getItem('user_role');
  const effectiveAuth = auth?.token
    ? auth
    : (persistedToken ? { token: persistedToken, role: persistedRole } : null);

  if (!effectiveAuth?.token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (effectiveAuth.role !== allowedRole) {
    return <Navigate to={defaultRouteForRole(effectiveAuth.role)} replace />;
  }

  return children;
}

function NotFound() {
  return (
    <div style={{minHeight:"60vh",display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{background:"#fff",borderRadius:16,boxShadow:"0 8px 18px rgba(0,0,0,0.08)",padding:24}}>
        <h1 style={{margin:0}}>Page not found</h1>
      </div>
    </div>
  );
}

function AppShell() {
  const { auth, logoutUser } = useAuth();
  const { chatDrawerOpen, setChatDrawerOpen } = useChat();
  const location = useLocation();
  const navigate = useNavigate();

  // Hide the old Navbar on BigPicture Home routes
  const hideGlobalNavbar = false;
  const helpDrawerOpen = location.pathname === '/help';
  const backgroundLocation = location.state?.backgroundLocation;
  const helpSection = location.state?.helpSection || location.hash?.replace('#', '') || '';
  const routedLocation = helpDrawerOpen
    ? (backgroundLocation || { pathname: '/', search: '', hash: '', state: null, key: 'help-background' })
    : location;

  const handleCloseHelpDrawer = () => {
    if (helpDrawerOpen && backgroundLocation) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  return (
    <SessionProvider warnMs={2 * 60 * 1000}>
      <GlobalBlurredCollage />
      {!hideGlobalNavbar && <Navbar isAuthenticated={!!auth} onLogout={logoutUser} />}

      <AppErrorBoundary>
        <Routes location={routedLocation}>
          <Route path="/" element={<HomePage initialSection="intro" />} />
          <Route path="/discover" element={<HomePage initialSection="discover" />} />

          {/* Other routes */}
          <Route path="/artists/:id" element={<ArtistProfilePageBigPicture />} />
          <Route path="/hosts/:id" element={<HostProfilePageBigPicture />} />
          <Route path="/host" element={<HostDashboard />} />
          <Route
            path="/admin"
            element={(
              <RequireRole auth={auth} allowedRole="admin">
                <AdminPage />
              </RequireRole>
            )}
          />
          <Route
            path="/admin/artists"
            element={(
              <RequireRole auth={auth} allowedRole="admin">
                <ArtistManager />
              </RequireRole>
            )}
          />
          <Route
            path="/admin/hosts"
            element={(
              <RequireRole auth={auth} allowedRole="admin">
                <HostManager />
              </RequireRole>
            )}
          />
          <Route
            path="/admin/buyers"
            element={(
              <RequireRole auth={auth} allowedRole="admin">
                <BuyerManager />
              </RequireRole>
            )}
          />
          <Route
            path="/admin/artworks"
            element={(
              <RequireRole auth={auth} allowedRole="admin">
                <ArtworkManager />
              </RequireRole>
            )}
          />
          <Route path="/hosts" element={<Navigate to="/admin/hosts" replace />} />
          <Route path="/buyers" element={<Navigate to="/admin/buyers" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reset-password/:uid/:token" element={<ResetPasswordConfirmPage />} />
          <Route
            path="/dashboard/artist"
            element={(
              <RequireRole auth={auth} allowedRole="artist">
                <ArtistDashboard />
              </RequireRole>
            )}
          />
          <Route
            path="/dashboard/host"
            element={(
              <RequireRole auth={auth} allowedRole="host">
                <HostDashboard />
              </RequireRole>
            )}
          />
          <Route
            path="/dashboard/buyer"
            element={(
              <RequireRole auth={auth} allowedRole="buyer">
                <BuyerDashboard />
              </RequireRole>
            )}
          />
          <Route path="/artworks/create" element={<ArtworkCreatePage />} />
          <Route path="/artist-profile/setup" element={<ArtistProfileSetupPage />} />
          <Route path="/host-profile/setup" element={<HostProfileSetupPage />} />
          <Route path="/buyer-profile/setup" element={<BuyerProfileSetupPage />} />
          <Route path="/host/requests" element={<HostRequestsPage />} />
          <Route path="/host/nearby-artworks" element={<NearbyArtworksPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<div style={{ padding: 24 }}>Checkout canceled.</div>} />
          <Route path="/buyers/purchases" element={<MyPurchases />} />
          <Route path="/artworks/:id" element={<ArtworkDetail />} />
          <Route path="/complete-signup" element={<CompleteSignupPage />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AppErrorBoundary>

      <MessagesDrawer open={chatDrawerOpen} onClose={() => setChatDrawerOpen(false)} />
      <HelpDrawer open={helpDrawerOpen} onClose={handleCloseHelpDrawer} sectionId={helpSection} />
    </SessionProvider>
  );
}

export default function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}
